import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

import {
  FaArrowLeft,
  FaSpinner,
  FaCheck,
  FaTimes,
  FaClock,
  FaPlus,
  FaBan,
  FaTicketAlt,
  FaChartLine,
  FaCoins,
  FaLayerGroup,
  FaCalendarAlt,
  FaChevronRight,
  FaTrophy,
  FaExclamationTriangle,
} from "react-icons/fa";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");

  return token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};
};

const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [isAllowed, setIsAllowed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /*
  |--------------------------------------------------------------------------
  | FETCH PROJECT + TICKETS
  |--------------------------------------------------------------------------
  */

  const fetchProject = async () => {
    try {
      setLoading(true);
      setError("");

      const headers = {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      };

      const [
        projectResponse,
        ticketsResponse,
        allowedResponse,
      ] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/projects/${id}`,
          { headers }
        ),

        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/tickets/project/${id}`,
          { headers }
        ),

        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/projects/isallowed?projectId=${id}`,
          { headers }
        ),
      ]);

      // Project
      setProject(
        projectResponse.data.project ||
          projectResponse.data
      );

      // Tickets
      setTickets(
        ticketsResponse.data.tickets || []
      );

      // Ticket creation eligibility
      setIsAllowed(
        allowedResponse.data.isAllowed
      );

    } catch (err) {
      console.error("Failed to fetch project:", err);

      setError(
        err.response?.data?.error ||
          "Failed to load project. Please try again."
      );

      setTickets([]);
      setIsAllowed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProject();
  }, [id]);

  /*
  |--------------------------------------------------------------------------
  | HELPERS
  |--------------------------------------------------------------------------
  */

  const formatMoney = (value) => {
    if (value === null || value === undefined) {
      return "-";
    }

    return Number(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (date) => {
    if (!date) {
      return "-";
    }

    try {
      return new Date(date).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "-";
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case "won":
        return {
          label: "Won",
          color:
            "bg-green-900/60 text-green-300 border-green-500/30",
          icon: <FaCheck />,
        };

      case "lost":
        return {
          label: "Lost",
          color:
            "bg-red-900/60 text-red-300 border-red-500/30",
          icon: <FaTimes />,
        };

      case "cancelled":
        return {
          label: "Cancelled",
          color:
            "bg-gray-700/60 text-gray-300 border-gray-500/30",
          icon: <FaBan />,
        };

      default:
        return {
          label: "Active",
          color:
            "bg-blue-900/60 text-blue-300 border-blue-500/30",
          icon: <FaClock />,
        };
    }
  };

  const getTicketStatusConfig = (status) => {
    switch (status) {
      case "won":
        return "bg-green-900/50 text-green-300";

      case "lost":
        return "bg-red-900/50 text-red-300";

      case "cancelled":
        return "bg-gray-700 text-gray-300";

      case "void":
        return "bg-gray-700 text-gray-300";

      default:
        return "bg-blue-900/50 text-blue-300";
    }
  };

  const handleNewTicket = () => {
    if (!project) {
        alert("Please select a project first.");
        return;
    }

    navigate("/tickets/new", {
        state: {
        project: project,
        },
    });
  };

  /*
  |--------------------------------------------------------------------------
  | LOADING
  |--------------------------------------------------------------------------
  */

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
          <div className="text-center">
            <FaSpinner className="inline-block animate-spin text-4xl text-green-400 mb-4" />

            <p className="text-gray-300">
              Loading project...
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | ERROR
  |--------------------------------------------------------------------------
  */

  if (error || !project) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900">
          <div className="max-w-4xl mx-auto p-4 md:p-6">

            <button
              onClick={() => navigate("/projects")}
              className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
            >
              <FaArrowLeft />
              Back to Projects
            </button>

            <div className="bg-gray-800/60 border border-red-500/20 rounded-xl p-8 text-center">

              <FaExclamationTriangle className="text-red-400 text-4xl mx-auto mb-4" />

              <h2 className="text-xl font-semibold text-white">
                Unable to load project
              </h2>

              <p className="text-gray-400 mt-2">
                {error || "Project not found."}
              </p>

              <button
                onClick={fetchProject}
                className="mt-6 px-5 py-2.5 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-500 hover:to-emerald-500"
              >
                Try Again
              </button>

            </div>
          </div>
        </div>
      </Layout>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | PROJECT DATA
  |--------------------------------------------------------------------------
  */

  const status = getStatusConfig(project.status);

  const steps =
    project.steps ||
    project.project_steps ||
    [];


  const wins =
    project.wins ??
    tickets.filter(
      (ticket) => ticket.status === "won",
    ).length;

  const losses =
    project.loses ??
    tickets.filter(
      (ticket) => ticket.status === "lost",
    ).length;

  const completedTickets = wins + losses;

  const winRate =
    completedTickets > 0
      ? ((wins / completedTickets) * 100).toFixed(1)
      : "0.0";

  /*
  |--------------------------------------------------------------------------
  | RENDER
  |--------------------------------------------------------------------------
  */

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900">

        <div className="max-w-7xl mx-auto p-4 md:p-6">

          {/* Back */}

          <button
            onClick={() => navigate("/projects")}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-5 transition-colors"
          >
            <FaArrowLeft />
            Back to Projects
          </button>

          {/* Header */}

          <div className="bg-gray-800/60 backdrop-blur-sm border border-green-500/20 rounded-xl overflow-hidden mb-6">

            <div className="p-5 md:p-6">

              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">

                <div>

                  <div className="flex items-center gap-3 flex-wrap">

                    <h1 className="text-2xl md:text-3xl font-bold text-white">
                      {project.name}
                    </h1>

                    <span
                      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm border ${status.color}`}
                    >
                      {status.icon}
                      {status.label}
                    </span>

                  </div>

                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-400">

                    <span className="capitalize">
                      {project.type} project
                    </span>

                    <span className="flex items-center gap-1">
                      <FaCalendarAlt />
                      {formatDate(project.created_at)}
                    </span>

                  </div>

                </div>

                {isAllowed && (
                  <button
                    onClick={handleNewTicket}
                    disabled={!project}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                        project
                        ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-500 hover:to-emerald-500"
                        : "bg-gray-700 text-gray-500 cursor-not-allowed"
                    }`}
                    >
                    <FaPlus className="inline mr-2" />
                    New Ticket
                  </button>
                )}
                
                {/* <button
                  onClick={() => navigate("/tickets")}
                  className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-500 hover:to-emerald-500 transition-all shadow-lg shadow-green-600/20 flex items-center justify-center gap-2"
                >
                  <FaTicketAlt />
                  View Tickets
                </button> */}

              </div>

            </div>

          </div>

          {/* Statistics */}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">

            {/* Wins */}

            <div className="bg-gray-800/60 border border-green-500/20 rounded-xl p-4">

              <div className="flex items-center justify-between">

                <div>
                  <p className="text-sm text-gray-400">
                    Wins
                  </p>

                  <p className="text-2xl font-bold text-green-400 mt-1">
                    {wins}
                  </p>
                </div>

                <div className="w-10 h-10 rounded-lg bg-green-900/40 flex items-center justify-center">
                  <FaTrophy className="text-green-400" />
                </div>

              </div>

            </div>

            {/* Losses */}

            <div className="bg-gray-800/60 border border-red-500/20 rounded-xl p-4">

              <div className="flex items-center justify-between">

                <div>
                  <p className="text-sm text-gray-400">
                    Losses
                  </p>

                  <p className="text-2xl font-bold text-red-400 mt-1">
                    {losses}
                  </p>
                </div>

                <div className="w-10 h-10 rounded-lg bg-red-900/40 flex items-center justify-center">
                  <FaTimes className="text-red-400" />
                </div>

              </div>

            </div>

            {/* Win Rate */}

            <div className="bg-gray-800/60 border border-blue-500/20 rounded-xl p-4">

              <div className="flex items-center justify-between">

                <div>
                  <p className="text-sm text-gray-400">
                    Win Rate
                  </p>

                  <p className="text-2xl font-bold text-blue-400 mt-1">
                    {winRate}%
                  </p>
                </div>

                <div className="w-10 h-10 rounded-lg bg-blue-900/40 flex items-center justify-center">
                  <FaChartLine className="text-blue-400" />
                </div>

              </div>

            </div>

            {/* Tickets */}

            <div className="bg-gray-800/60 border border-purple-500/20 rounded-xl p-4">

              <div className="flex items-center justify-between">

                <div>
                  <p className="text-sm text-gray-400">
                    Tickets
                  </p>

                  <p className="text-2xl font-bold text-purple-400 mt-1">
                    {tickets.length}
                  </p>
                </div>

                <div className="w-10 h-10 rounded-lg bg-purple-900/40 flex items-center justify-center">
                  <FaTicketAlt className="text-purple-400" />
                </div>

              </div>

            </div>

          </div>

          {/* Project Configuration */}

          <div className="bg-gray-800/60 backdrop-blur-sm border border-green-500/20 rounded-xl mb-6 overflow-hidden">

            <div className="px-5 py-4 border-b border-green-500/20">

              <h2 className="font-semibold text-white flex items-center gap-2">
                <FaCoins className="text-green-400" />
                Project Configuration
              </h2>

            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-5">

              <div>
                <p className="text-xs text-gray-500 mb-1">
                  Initial Stake
                </p>

                <p className="text-white font-semibold">
                  {formatMoney(project.initial_stake)}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-500 mb-1">
                  Target Odd
                </p>

                <p className="text-white font-semibold">
                  {project.target_odd ?? "-"}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-500 mb-1">
                  Current Stake
                </p>

                <p className="text-white font-semibold">
                  {formatMoney(project.current_stake)}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-500 mb-1">
                  Current Step
                </p>

                <p className="text-white font-semibold">
                  {project.current_step ?? 0}

                  {project.total_steps
                    ? ` / ${project.total_steps}`
                    : ""}
                </p>
              </div>

            </div>

          </div>

          {/* Steps */}

          {steps.length > 0 && (
            <div className="bg-gray-800/60 backdrop-blur-sm border border-green-500/20 rounded-xl mb-6 overflow-hidden">

              <div className="px-5 py-4 border-b border-green-500/20">

                <h2 className="font-semibold text-white flex items-center gap-2">
                  <FaLayerGroup className="text-green-400" />
                  Project Steps
                </h2>

              </div>

              <div className="overflow-x-auto">

                <table className="w-full">

                  <thead className="bg-gray-700/30">

                    <tr>

                      <th className="text-left px-5 py-3 text-sm text-gray-400">
                        Step
                      </th>

                      <th className="text-left px-5 py-3 text-sm text-gray-400">
                        Stake
                      </th>

                      <th className="text-left px-5 py-3 text-sm text-gray-400">
                        Target Odd
                      </th>

                      <th className="text-left px-5 py-3 text-sm text-gray-400">
                        Status
                      </th>

                      <th className="text-right px-5 py-3 text-sm text-gray-400">
                        Ticket
                      </th>

                    </tr>

                  </thead>

                  <tbody>

                    {steps.map((step) => {

                      const stepTicket = tickets.find(
                        (ticket) =>
                          Number(ticket.step_id) ===
                          Number(step.id),
                      );

                      return (
                        <tr
                          key={step.id}
                          className="border-t border-green-500/10 hover:bg-gray-700/20"
                        >

                          <td className="px-5 py-4 text-white font-medium">
                            #{step.step_number}
                          </td>

                          <td className="px-5 py-4 text-gray-300">
                            {formatMoney(step.stake)}
                          </td>

                          <td className="px-5 py-4 text-gray-300">
                            {step.target_odd}
                          </td>

                          <td className="px-5 py-4">

                            <span
                              className={`px-2.5 py-1 rounded-full text-xs capitalize ${
                                step.status === "won"
                                  ? "bg-green-900/50 text-green-300"
                                  : step.status === "lost"
                                    ? "bg-red-900/50 text-red-300"
                                    : step.status === "active"
                                      ? "bg-blue-900/50 text-blue-300"
                                      : "bg-gray-700 text-gray-300"
                              }`}
                            >
                              {step.status}
                            </span>

                          </td>

                          <td className="px-5 py-4 text-right">

                            {stepTicket ? (
                              <button
                                onClick={() =>
                                  navigate(
                                    `/tickets/${stepTicket.id}`,
                                  )
                                }
                                className="text-green-400 hover:text-green-300 text-sm inline-flex items-center gap-1"
                              >
                                View
                                <FaChevronRight size={10} />
                              </button>
                            ) : (
                              <span className="text-gray-600">
                                -
                              </span>
                            )}

                          </td>

                        </tr>
                      );
                    })}

                  </tbody>

                </table>

              </div>

            </div>
          )}

          {/* Tickets */}

          <div className="bg-gray-800/60 backdrop-blur-sm border border-green-500/20 rounded-xl overflow-hidden">

            <div className="px-5 py-4 border-b border-green-500/20 flex items-center justify-between">

              <h2 className="font-semibold text-white flex items-center gap-2">
                <FaTicketAlt className="text-green-400" />
                Project Tickets
              </h2>

              <span className="text-xs bg-gray-700 px-2.5 py-1 rounded-full text-gray-300">
                {tickets.length}
              </span>

            </div>

            {tickets.length > 0 ? (

              <div className="divide-y divide-green-500/10">

                {tickets.map((ticket) => (

                  <button
                    key={ticket.id}
                    onClick={() =>
                      navigate(`/tickets/${ticket.id}`)
                    }
                    className="w-full text-left p-4 hover:bg-gray-700/20 transition-colors"
                  >

                    <div className="flex items-center justify-between gap-4">

                      <div className="flex items-center gap-3 min-w-0">

                        <div className="w-10 h-10 rounded-lg bg-gray-700 flex items-center justify-center flex-shrink-0">
                          <FaTicketAlt className="text-green-400" />
                        </div>

                        <div className="min-w-0">

                          <p className="text-white font-medium">
                            Ticket #
                            {ticket.ticket_number ||
                              ticket.id}
                          </p>

                          <p className="text-sm text-gray-400 mt-1">
                            Stake{" "}
                            {formatMoney(ticket.stake)}
                            {" • "}
                            Odd{" "}
                            {Number(
                              ticket.total_odd || 0,
                            ).toFixed(2)}
                          </p>

                          {ticket.step_number !== null &&
                            ticket.step_number !==
                              undefined && (
                              <p className="text-xs text-gray-500 mt-1">
                                Step #{ticket.step_number}
                              </p>
                            )}

                        </div>

                      </div>

                      <div className="flex items-center gap-3 flex-shrink-0">

                        <span
                          className={`px-2.5 py-1 rounded-full text-xs capitalize ${getTicketStatusConfig(
                            ticket.status,
                          )}`}
                        >
                          {ticket.status}
                        </span>

                        <FaChevronRight className="text-gray-500" />

                      </div>

                    </div>

                  </button>

                ))}

              </div>

            ) : (

              <div className="p-10 text-center">

                <FaTicketAlt className="text-gray-600 text-3xl mx-auto mb-3" />

                <p className="text-gray-400">
                  No tickets in this project yet.
                </p>

              </div>

            )}

          </div>

        </div>

      </div>
    </Layout>
  );
};

export default ProjectDetails;